import { useState, useEffect } from 'react'
import { serverSyncService } from '../../core/infrastructure/server-sync-service'
import { connectionStateService, type SyncStatus } from '../../core/infrastructure/connection-state-service'
import { localDB, type SyncLogEntry, type DeadLetterItem, type SyncQueueItem } from '../../db/client'

export type EnrichedSyncQueueItem = SyncQueueItem & { uiName?: string }

export function useSyncStatus() {
  const [pendingSyncCount, setPendingSyncCount] = useState(serverSyncService.getDisplayedPendingCount())
  const [pendingSyncItems, setPendingSyncItems] = useState<EnrichedSyncQueueItem[]>([])
  const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([])
  const [showSyncDetails, setShowSyncDetails] = useState(false)
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(connectionStateService.getSyncStatus())
  const [failedItems, setFailedItems] = useState<DeadLetterItem[]>([])
  const [isRefreshingScenarios, setIsRefreshingScenarios] = useState(serverSyncService.isUserGroupSyncInProgress())

  const enrichItems = async (items: SyncQueueItem[]): Promise<EnrichedSyncQueueItem[]> => {
    return Promise.all(items.map(async (item) => {
      const enriched = { ...item } as EnrichedSyncQueueItem
      const cmd = item.command
      
      // Try to find name if missing in command
      // Main use case: CompleteItem which only has itemId
      if (cmd.commandType.includes('Item')) {
         const hasName = ('itemName' in cmd && !!cmd.itemName) || ('newName' in cmd && !!cmd.newName)
         if (!hasName && 'itemId' in cmd) {
            try {
               const savedItem = await localDB.getListItem(cmd.itemId as string)
               if (savedItem) {
                  enriched.uiName = savedItem.name
               }
            } catch (err) {
               // ignore
            }
         }
      } else if (cmd.commandType.includes('List')) {
          const hasName = ('listName' in cmd && !!cmd.listName) || ('newName' in cmd && !!cmd.newName)
          if (!hasName && 'listId' in cmd) {
            try {
                const savedList = await localDB.getList(cmd.listId as string)
                if (savedList) {
                   enriched.uiName = savedList.name
                }
             } catch (err) {
                // ignore
             }
          }
      }
      
      return enriched
    }))
  }

  const handleSyncBadgeClick = async () => {
    try {
      const items = await serverSyncService.getPendingItems()
      const enrichedItems = await enrichItems(items)
      setPendingSyncItems(enrichedItems)
      
      // Load recent sync log (last 50 entries)
      const log = await localDB.getSyncLog(50)
      setSyncLog(log)
      
      setShowSyncDetails(true)
    } catch (err) {
      console.error('[useSyncStatus] Error loading sync details:', err)
    }
  }

  const refreshPendingItems = async () => {
    try {
      const items = await serverSyncService.getPendingItems()
      const enrichedItems = await enrichItems(items)
      setPendingSyncItems(enrichedItems)
    } catch (err) {
      console.debug('[useSyncStatus] DB not ready for getPendingItems')
    }
  }

  const refreshSyncLog = async () => {
    try {
      const log = await localDB.getSyncLog(50)
      setSyncLog(log)
    } catch (err) {
      console.debug('[useSyncStatus] DB not ready for getSyncLog')
    }
  }

  const refreshFailedItems = async () => {
    try {
      const items = await localDB.getDeadLetterQueue()
      setFailedItems(items)
    } catch (err) {
      // DB not initialized yet - ignore
      console.debug('[useSyncStatus] DB not ready for getDeadLetterQueue')
    }
  }

  const formatSyncItemDescription = (item: EnrichedSyncQueueItem) => {
    // metadata-first approach
    if (item.uiMetadata?.description && item.uiMetadata?.entityName) {
        return `${item.uiMetadata.description}: ${item.uiMetadata.entityName}`
    }
    if (item.uiMetadata?.entityName) {
       // Just name is known, e.g. "Update item: Buy Milk"
       // We'll calculate action below but use this name
    }

    const cmd = item.command
    let action = 'Update'
    let entityType = 'Scenario'
    let name = item.uiName || item.uiMetadata?.entityName || ''
    
    // Determine action
    if (cmd.commandType.startsWith('Create')) action = 'Create'
    else if (cmd.commandType.startsWith('Delete')) action = 'Delete'
    else if (cmd.commandType === 'CompleteItem') action = 'Complete'
    
    // Determine entity type and name
    if (cmd.commandType.includes('Item')) {
      entityType = 'Item'
      if (!name) {
        if ('itemName' in cmd && cmd.itemName) name = cmd.itemName
        else if ('newName' in cmd && cmd.newName) name = cmd.newName
      }
      // Don't fall back to ID for name in the main description if possible, 
      // or make it clear it's an ID if we must.
    } else if (cmd.commandType.includes('List')) {
      entityType = 'List'
      if (!name) {
        if ('listName' in cmd && cmd.listName) name = cmd.listName
        else if ('newName' in cmd && cmd.newName) name = cmd.newName
      }
    } else if (cmd.commandType.includes('Scenario')) {
      entityType = 'Scenario'
      if (!name) {
        if ('name' in cmd && cmd.name) name = cmd.name
        else if ('newName' in cmd && cmd.newName) name = cmd.newName
      }
    }
    
    // Fallback for metadata description only
    if (!name && item.uiMetadata?.description) {
        return item.uiMetadata.description
    }

    if (name) {
      return `${action} ${entityType}: ${name}`
    }
    
    return `${action} ${entityType}`
  }

  useEffect(() => {
    // Subscribe to pending count changes from serverSyncService
    const unsubscribePendingCount = serverSyncService.onPendingCountChange(setPendingSyncCount)
    
    return unsubscribePendingCount
  }, [])

  useEffect(() => {
    const unsubscribeStatus = connectionStateService.onStatusChange(setSyncStatus)
    return unsubscribeStatus
  }, [])
  
  useEffect(() => {
    // Subscribe to user group sync state changes (scenario list refresh)
    const unsubscribeUserGroupSync = serverSyncService.onUserGroupSyncChange(setIsRefreshingScenarios)
    return unsubscribeUserGroupSync
  }, [])

  useEffect(() => {
    refreshFailedItems()

    const unsubscribe = serverSyncService.onSyncDiscard(() => {
      refreshFailedItems()
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    if (showSyncDetails) {
      refreshFailedItems()
    }
  }, [showSyncDetails])

  useEffect(() => {
    const unsubscribe = serverSyncService.onSyncComplete(async () => {
      if (showSyncDetails) {
        await refreshPendingItems()
        await refreshSyncLog()
      }
    })
    return unsubscribe
  }, [showSyncDetails])

  const retryFailedItem = async (item: DeadLetterItem) => {
    const syncItem = await localDB.retryDeadLetterItem(item.commandId)
    if (syncItem) {
      await Promise.all([
        refreshFailedItems(),
        refreshPendingItems()
      ])
      void serverSyncService.sync()
    }
  }

  const getEntityInfoFromCommand = (item: DeadLetterItem) => {
    const cmd = item.command
    let type: 'create' | 'update' | 'delete' | 'leave' = 'update'
    let entity: 'scenario' | 'list' | 'item' = 'scenario'
    let entityId = cmd.scenarioId
    let entityName = cmd.scenarioId
    
    if (cmd.commandType.startsWith('Create')) type = 'create'
    else if (cmd.commandType.startsWith('Delete')) type = 'delete'
    
    if (cmd.commandType.includes('Item')) {
      entity = 'item'
      entityId = 'itemId' in cmd ? cmd.itemId : cmd.scenarioId
      entityName = 'itemName' in cmd ? cmd.itemName : ('newName' in cmd ? cmd.newName : entityId)
    } else if (cmd.commandType.includes('List')) {
      entity = 'list'
      entityId = 'listId' in cmd ? cmd.listId : cmd.scenarioId
      entityName = 'listName' in cmd ? cmd.listName : ('newName' in cmd ? cmd.newName : entityId)
    } else if (cmd.commandType.includes('Scenario')) {
      entity = 'scenario'
      entityId = cmd.scenarioId
      entityName = 'name' in cmd ? cmd.name : ('newName' in cmd ? cmd.newName : entityId)
    }
    
    return { type, entity, entityId, entityName }
  }

  const discardFailedItem = async (item: DeadLetterItem) => {
    const entityInfo = getEntityInfoFromCommand(item)
    await localDB.discardDeadLetterItem(item, {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      ...entityInfo,
      status: 'discarded',
      error: item.failureReason,
      retries: item.retries
    })

    await refreshFailedItems()
    await refreshSyncLog()
    serverSyncService.requestImmediateSync()
  }

  return {
    pendingSyncCount,
    pendingSyncItems,
    syncLog,
    showSyncDetails,
    setShowSyncDetails,
    handleSyncBadgeClick,
    refreshPendingItems,
    refreshSyncLog,
    formatSyncItemDescription,
    syncStatus,
    failedItems,
    refreshFailedItems,
    retryFailedItem,
    discardFailedItem,
    isRefreshingScenarios,
  }
}
